import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button as NewButton } from "@/components/ui/NewButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FancyMultiSelect } from "../ui/fancy-multi-select";
import { getUniqueRecipients, getUniqueSenders, getUniqueTags } from "@/pages/matter/[slug]/case-files/correspondence";
import { backendClient } from "@/lib/api-client";
import { useParams } from '@tanstack/react-router';
import { toast } from 'react-hot-toast';

type Options = Record<"value" | "label", string>;

const Option = z.object({
  value: z.string(),
  label: z.string(),
});

const Attachment = z.object({
  guid: z.string(),
  fileName: z.string(),
  token_count: z.number().optional(),
}).passthrough();

const formSchema = z.object({
  date: z.union([z.date(), z.string()]).optional().nullable(),
  from: z.array(Option),
  to: z.array(Option),
  summary: z.string().optional(),
  tags: z.array(Option).optional(),
  attachments: z.array(Attachment).optional(),
  is_draft: z.enum(["true", "false"]),
});

function convertToOptions(arr: string[]): Options[] {
  return arr.map(item => ({
    value: item,
    label: item
  }));
}

interface TagData {
  name: string;
  description: string;
}

interface EditCorrespondenceDialogProps {
  row: any;
  onSuccess: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export function EditCorrespondenceDialog({ row, onSuccess, isOpen, onClose }: EditCorrespondenceDialogProps) {
  const { slug } = useParams({ from: '/matter/$slug' });
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [matterTags, setMatterTags] = useState<Options[]>([]);
  const finalStatusDisabled = row.is_draft && row.is_drafting_managed;


  // Fetch matter tags when dialog opens
  useEffect(() => {
    const fetchTags = async () => {
      if (isOpen && slug) {
        try {
          const response = await backendClient.get(`/correspondence/${slug}/get-tags`);

          // Convert tags to Options format with descriptions
          if (response.data && response.data.tags) {
            const tagOptions = response.data.tags.map((tag: TagData) => ({
              value: tag.name,
              label: tag.name
            }));
            setMatterTags(tagOptions);
          }
        } catch (error) {
          console.error('Error fetching matter tags:', error);
        }
      }
    };

    fetchTags();
  }, [isOpen, slug]);

  // Helper to get a valid date or null
  const getValidDate = (date: any): Date | null => {
    if (!date) return null;
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: getValidDate(row.date),
      from: convertToOptions(row.from),
      to: convertToOptions(row.to),
      summary: row.summary,
      tags: convertToOptions(row.tags),
      attachments: row.attachments,
      is_draft: row.is_draft ? "true" : "false",
    },
  });

  const handleAddAttachment = () => {
    setAttachments([...attachments, ""]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Format date exactly as selected, no timezone conversion
      // Handle null/undefined dates
      let formattedDate: string | null = null;
      if (data.date) {
        if (data.date instanceof Date) {
          // Use local date components to avoid timezone shifts
          const year = data.date.getFullYear();
          const month = String(data.date.getMonth() + 1).padStart(2, '0');
          const day = String(data.date.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else if (typeof data.date === 'string') {
          formattedDate = data.date.split('T')[0];
        }
      }

      const isDraft = data.is_draft === "true";

      const metadata = {
        date: formattedDate, // Can be null if no date selected
        summary: data.summary || "",
        sender: data.from.map((option: Options) => option.value),
        recipient: data.to.map((option: Options) => option.value),
        deadlines: [], // Add deadlines logic if needed
      };

      // Extract tags separately
      const newTags = data.tags?.map((option: Options) => option.value) || [];
      const originalTags = row.tags || [];

      // Check if tags have changed
      const tagsChanged = JSON.stringify(newTags.sort()) !== JSON.stringify(originalTags.sort());

      const requestBody: any = { metadata };
      if (tagsChanged) {
        requestBody.tags = newTags;
      }

      await backendClient.put(
        `/correspondence/${slug}/edit-metadata`,
        requestBody,
        { params: { document_guid: row.guid } }
      );

      if (isDraft !== row.is_draft) {
        await backendClient.post(`/correspondence/${slug}/toggle-draft-status`, {
          document_guids: [row.guid],
          is_draft: isDraft,
        });
      }

      // Success - close dialog and show success message
      toast.success("Correspondence row updated");

      // Call the onSuccess callback to refresh data if provided
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }

      // Close the dialog
      onClose();
    } catch (error) {
      console.error('Error updating correspondence:', error);
      toast.error("We were unable to update the correspondence row. Please reach out to support@crimson.law.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          form.reset({
            date: getValidDate(row.date),
            from: convertToOptions(row.from),
            to: convertToOptions(row.to),
            summary: row.summary,
            tags: convertToOptions(row.tags),
            attachments: row.attachments,
            is_draft: row.is_draft ? "true" : "false",
          });
          onClose();
        }
      }}
    >
      <DialogContent
        className="min-w-fit"
        onInteractOutside={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Correspondence Details</DialogTitle>
          <DialogDescription>Update the correspondence details below.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4 pt-4">
            <div className="flex flex-col gap-4">
              <FormField
                name="date"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Date</FormLabel>
                    <DatePicker
                      initialValue={field.value ? getValidDate(field.value) ?? undefined : undefined}
                      onChange={field.onChange}
                      disabledDates={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      showTopControls
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* From Field */}
              <FormField
                name="from"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>From</FormLabel>
                    <FormControl>
                      <FancyMultiSelect
                        freeText={true}
                        value={field.value}
                        onValueChange={field.onChange}
                        expandOnFocus={false}
                        options={Array.from(new Set([...getUniqueSenders(), ...getUniqueRecipients()].map(o => o.value))).map(value => ({
                          value,
                          label: value
                        }))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* To Field */}
              <FormField
                name="to"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>To</FormLabel>
                    <FormControl>
                      <FancyMultiSelect
                        freeText={true}
                        value={field.value}
                        onValueChange={field.onChange}
                        expandOnFocus={false}
                        options={Array.from(new Set([...getUniqueSenders(), ...getUniqueRecipients()].map(o => o.value))).map(value => ({
                          value,
                          label: value
                        }))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Summary Field */}
              <FormField
                name="summary"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel htmlFor="summary">
                      Summary
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} id="summary" className="min-h-[150px] custom-scrollbar" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Status Field */}
              <FormField
                name="is_draft"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="true">Draft</SelectItem>
                        <SelectItem value="false" disabled={finalStatusDisabled}>Final</SelectItem>
                      </SelectContent>
                    </Select>
                    {finalStatusDisabled && (
                      <p className="text-xs text-slate-500">
                        Drafts created in Drafting cannot currently be marked final.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Tags Field */}
              <FormField
                name="tags"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
	                      <FancyMultiSelect
	                        value={field.value ?? []}
	                        onValueChange={field.onChange}
                        expandOnFocus={false}
                        options={matterTags.length > 0 ? matterTags : getUniqueTags()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-2">
              <div className="flex flex-row gap-2">
                <NewButton
                  variant='outline'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.reset();
                    onClose();
                  }}
                  disabled={isSubmitting}
                  text='Cancel'
                />
                <NewButton
                  type="submit"
                  loading={isSubmitting}
                  loadingText='Saving'
                  text='Save changes'
                />
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
